import { useRef, useState } from "react";
import { invalidateApiCache } from "@shared/api";
import { shouldReduceMotion } from "@shared/lib/motion";
import { DashboardPanels } from "@features/dashboard/ui/DashboardPanels";
import { useDashboardState } from "@features/dashboard/model/useDashboardState";
import type { HeroKey, SectionKey } from "@features/dashboard/model/types";
import { AppHeader } from "./components/AppHeader";
import { AppToolbar } from "./components/AppToolbar";
import { NewMatchesNotice } from "./components/NewMatchesNotice";
import styles from "./App.module.css";

export function App() {
  const [activeSection, setActiveSection] = useState<SectionKey>("overview");
  const [expandedHero, setExpandedHero] = useState<HeroKey | null>(null);
  const matchesSectionRef = useRef<HTMLElement | null>(null);
  const {
    current,
    health,
    allMatches,
    matches,
    range,
    setRange,
    from,
    useDailyBucket,
    displayError,
    refreshKey,
    newMatchIds,
    newMatchNoticeCount,
    isRefreshing,
    lastRefreshAt,
    refreshNow,
    clearNewMatchNotice,
  } = useDashboardState({ activeSection, matchesSectionRef });

  function handleRefresh() {
    invalidateApiCache();
    void refreshNow();
  }

  function handleShowNewMatches() {
    clearNewMatchNotice();
    setActiveSection("matches");
    window.requestAnimationFrame(() => {
      matchesSectionRef.current?.scrollIntoView({ block: "start", behavior: shouldReduceMotion() ? "auto" : "smooth" });
    });
  }

  return (
    <div className={styles.app}>
      <AppHeader
        current={current}
        health={health}
        isRefreshing={isRefreshing}
        lastRefreshAt={lastRefreshAt}
        onRefresh={handleRefresh}
      />

      <AppToolbar
        activeSection={activeSection}
        health={health}
        newMatchNoticeCount={newMatchNoticeCount}
        range={range}
        onChangeSection={setActiveSection}
        onChangeRange={setRange}
      />

      {displayError && <div className={styles.error}>Error: {displayError}</div>}

      <NewMatchesNotice
        count={newMatchNoticeCount}
        visible={activeSection !== "matches"}
        onShow={handleShowNewMatches}
      />

      <DashboardPanels
        activeSection={activeSection}
        current={current}
        allMatches={allMatches}
        matches={matches}
        range={range}
        from={from}
        useDailyBucket={useDailyBucket}
        refreshKey={refreshKey}
        expandedHero={expandedHero}
        onToggleExpandedHero={(key) => setExpandedHero((prev) => (prev === key ? null : key))}
        matchesSectionRef={matchesSectionRef}
        newMatchIds={newMatchIds}
      />
    </div>
  );
}
